/**
 * Random.js
 * A deterministic Pseudo-Random Number Generator (PRNG).
 * Implements the Mulberry32 algorithm.
 */

export class Random {
    constructor(seed) {
        this.seed = seed || 12345;
        this.state = this.seed;
    }

    /**
     * Resets the generator with a new seed.
     * @param {number|string} seed
     */
    setSeed(seed) {
        if (typeof seed === 'string') {
            // Simple string hashing to number
            let h = 0x811c9dc5;
            for (let i = 0; i < seed.length; i++) {
                h ^= seed.charCodeAt(i);
                h = Math.imul(h, 0x01000193);
            }
            this.state = h >>> 0;
        } else {
            this.state = seed >>> 0;
        }
    }

    /**
     * Returns a float between 0 (inclusive) and 1 (exclusive).
     */
    next() {
        let t = this.state += 0x6D2B79F5;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }

    /**
     * Returns a boolean based on probability.
     * @param {number} probability - 0.0 to 1.0
     */
    bool(probability = 0.5) {
        return this.next() < probability;
    }

    /**
     * Returns a random integer between min (inclusive) and max (exclusive).
     */
    range(min, max) {
        return Math.floor(this.next() * (max - min)) + min;
    }

    /**
     * Returns a random element from an array.
     */
    pick(array) {
        if (!array || array.length === 0) return null;
        return array[Math.floor(this.next() * array.length)];
    }

    /**
     * Returns a number from a Gaussian (Bell Curve) distribution.
     * Uses the Box-Muller transform.
     * @param {number} mean - Center of the distribution
     * @param {number} stdev - Standard Deviation (spread)
     */
    gaussian(mean = 0, stdev = 1) {
        let u = 0, v = 0;
        while(u === 0) u = this.next(); // Converting [0,1) to (0,1)
        while(v === 0) v = this.next();

        // Box-Muller transform
        let z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
        return z * stdev + mean;
    }
}
